"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { capturePostHogEvent } from "@/lib/posthog";
import { readHomeVariant } from "@/lib/home-variant";
import { formatCategoryFallback } from "@/lib/cake-categories";

/**
 * Sentinel for "no filter applied".
 *
 * Underscores are stripped by `toCategorySlug`, so no real Firestore category —
 * including one literally named "All" — can ever produce this value and collide
 * with it.
 */
export const ALL_CATEGORIES = "__all__";

export interface CakeFilterCategory {
  /** Slug produced by `toCategorySlug` — must match the section's data-category. */
  slug: string;
  /** Raw Firestore category name, used for the untranslated-fallback label. */
  rawName: string;
  count: number;
}

interface CakeCategoryFilterProps {
  categories: CakeFilterCategory[];
  totalCount: number;
  /** Formatted cheapest cake price, e.g. "€ 40,00". Null when unknown. */
  lowestPrice: string | null;
  /**
   * The server-rendered cake sections, in display order. Passed as props rather
   * than rendered here so they stay Server Components: the cards must be in the
   * SSR HTML for SEO, and a client component cannot render them without pulling
   * the whole grid across the boundary.
   */
  sections: ReactNode[];
  /** The custom-cake tile, positioned by this component (see below). */
  customTile: ReactNode;
  /**
   * The cookie demand pretotype tile. Rendered immediately after the
   * custom-cake tile so the two share a row region and a scroll depth — the
   * comparison between them is the experiment's read, so their slots must not
   * drift apart.
   */
  cookieTile: ReactNode;
}

/**
 * Category chips for homepage variant B, wrapping the grid they filter.
 *
 * Filtering is done in CSS, not by re-rendering: this component only writes the
 * active slug to `data-filter`, and per-category rules emitted by `CakesGrid`
 * hide the sections that do not match. Doing it that way is what allows the
 * cake sections to remain server-rendered — and it means an interrupted filter
 * can never leave the page with cakes missing, because nothing was unmounted.
 *
 * In variant A the whole chip row is hidden by CSS (see app/globals.css), so
 * both arms ship identical markup and the control renders exactly as it does
 * today.
 */
export function CakeCategoryFilter({
  categories,
  totalCount,
  lowestPrice,
  sections,
  customTile,
  cookieTile,
}: CakeCategoryFilterProps) {
  const t = useTranslations("cakesGrid");
  // Same namespace + same fallback the section headings use, so a chip and the
  // heading it scrolls to can never disagree about a category's name.
  const tCategories = useTranslations("cakesGrid.categoryNames");
  const [active, setActive] = useState<string>(ALL_CATEGORIES);

  const labelFor = (category: CakeFilterCategory): string =>
    tCategories.has(category.slug as any)
      ? tCategories(category.slug as any)
      : formatCategoryFallback(category.rawName);

  const handleSelect = (slug: string) => {
    if (slug === active) return;
    setActive(slug);
    capturePostHogEvent("cake_filter_applied", {
      category: slug,
      surface: "homepage",
      home_variant: readHomeVariant(),
    });
  };

  const visibleCount =
    active === ALL_CATEGORIES
      ? totalCount
      : (categories.find((c) => c.slug === active)?.count ?? 0);

  const chips: Array<{ slug: string; label: string; count: number | null }> = [
    // "All" carries no count — the total is already stated in the header
    // beside the heading, and repeating it here would read as a duplicate.
    { slug: ALL_CATEGORIES, label: t("filterAll"), count: null },
    ...categories.map((category) => ({
      slug: category.slug,
      label: labelFor(category),
      count: category.count,
    })),
  ];

  const showingAll = active === ALL_CATEGORIES;

  return (
    <div data-filter={active}>
      {/*
        Deliberately compact. An earlier version wrapped the chips onto two rows
        with a separate count line below, which pushed the first cake image
        ~175px further down on mobile than the control — i.e. the challenger's
        above-the-fold was WORSE at the exact thing it exists to improve. One
        44px-tall scrolling row plus an inline count costs ~60px instead.
      */}
      <div className="cake-filter mx-auto max-w-[1536px] pt-5 md:pt-8">
        {/* Section header. Variant B has no per-category headings, so this is
            the grid's single heading — and the place the catalogue size and
            entry price are stated, which the rails never did anywhere. */}
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">
            {t("allCakesHeading")}
          </h2>
          <p className="text-xs text-muted-foreground sm:text-sm" aria-live="polite">
            {t("cakeCount", { count: visibleCount })}
            {lowestPrice !== null ? ` · ${t("priceFrom", { price: lowestPrice })}` : ""}
          </p>
        </div>
        {/*
          Underlined text tabs, not pills. Measured across current practice:
          every shipping design system specifies an 8–12px radius for a filter
          chip (M3 8dp, Polaris 8px, shadcn 8px) — a 999px capsule with a grey
          resting ring is a 2019-era signature and reads as a form control
          rather than navigation. Airbnb and Apple both drop the box entirely
          for a scrolling text row, which is also the lightest option
          vertically — the thing this variant can least afford to spend.
          Counts sit inside the label; a standalone count line under the row
          has no precedent in any reference measured.
        */}
        <div
          className="cake-filter-rail flex items-stretch gap-6 overflow-x-auto scrollbar-hide px-4 sm:px-6 lg:px-8"
          role="group"
          aria-label={t("filterAriaLabel")}
        >
          {chips.map((chip) => {
            const isActive = chip.slug === active;
            return (
              <button
                key={chip.slug}
                type="button"
                onClick={(event) => {
                  handleSelect(chip.slug);
                  // Bring a half-visible tab fully into view when chosen.
                  event.currentTarget.scrollIntoView({
                    inline: "center",
                    block: "nearest",
                    behavior: "smooth",
                  });
                }}
                aria-pressed={isActive}
                data-active={isActive ? "true" : undefined}
                className="cake-tab shrink-0 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {chip.label}
                {chip.count !== null && (
                  <span className="cake-tab-count">{chip.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/*
        The two non-cake tiles are positioned HERE, in the DOM, rather than
        being moved visually with CSS `order`. `order` never affects tab
        sequence or the accessibility tree, so an order-based fix would have
        left keyboard and screen-reader users meeting the tiles in a different
        place than sighted users see them (WCAG 1.3.2 / 2.4.3).

        Custom tile first, cookie tile second, always. The cookie tile is a
        pretotype for a product that does not exist; putting it ahead of a real
        revenue path would be optimising the experiment at the business's
        expense. Second place still lands it in the same row band on every
        breakpoint (2 / 3 / 4 columns), which is all the comparison needs.
      */}
      <div className="cakes-flow">
        {showingAll ? (
          <>
            {sections.slice(0, 1)}
            {customTile}
            {cookieTile}
            {sections.slice(1)}
          </>
        ) : (
          <>
            {sections}
            {customTile}
            {cookieTile}
          </>
        )}
      </div>
    </div>
  );
}
